// @flow
import React, { useState, useEffect, useRef, useCallback } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';

type Props = {
	realtimeStatus: string,
	styles: any,
	onTimeout?: Function,
};


function LoadingProgressIndicator({
	realtimeStatus,
	styles,
	onTimeout,
}: Props) {
	const [loadingProgress, setLoadingProgress] = useState(0);
	const timerRef = useRef(null);

	// Configuration constants
	const TIMER_INTERVAL = 450;
	const PROGRESS_INCREMENT = 1;
	const MAX_PROGRESS = 100;

	// Reusable function to start/restart the progress timer
	const startProgressTimer = useCallback(() => {
		// Clear any existing timer first
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}

		timerRef.current = setInterval(() => {
			setLoadingProgress((prevProgress) => {
				if (prevProgress >= MAX_PROGRESS) {
					return MAX_PROGRESS;
				}
				return prevProgress + PROGRESS_INCREMENT;
			});
		}, TIMER_INTERVAL);
	}, []);

	// Progress animation for the circular loader (45 seconds)
	useEffect(() => {
		startProgressTimer();

		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [startProgressTimer]);

	// Clear timer when progress reaches 100%
	useEffect(() => {
		if (loadingProgress >= MAX_PROGRESS && timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	}, [loadingProgress]);

	// Call onTimeout when loader times out (no real-time status and progress >= 100)
	useEffect(() => {
		if (!realtimeStatus && loadingProgress >= MAX_PROGRESS && onTimeout) {
			onTimeout();
		}
	}, [realtimeStatus, loadingProgress, onTimeout]);

	// Reset progress when status comes back after timeout
	useEffect(() => {
		// If we have realtimeStatus after being empty (recovery from timeout)
		if (realtimeStatus && loadingProgress >= MAX_PROGRESS) {
			// Reset progress and restart timer
			setLoadingProgress(0);
			startProgressTimer();
		}
	}, [realtimeStatus, loadingProgress, startProgressTimer]);

	return (
		<div style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center'
		}}>
			{/* Loader container with fixed positioning for text */}
			<div style={{ position: 'relative', display: 'inline-block' }}>
				<CircularProgress
					variant="determinate"
					value={loadingProgress}
					size={115}
					thickness={5.2}
					className={styles.circularLoader}
				/>
				<div style={{
					position: 'absolute',
					fontSize: '12px',
					color: loadingProgress >= 100 && !realtimeStatus ? '#f44336' : '#333',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: '80px',
					textAlign: 'center'
				}}>
					{loadingProgress >= MAX_PROGRESS && !realtimeStatus ? 'Timed out...' : 'Please wait...'}
				</div>
			</div>

			{/* Reserved space for error message to prevent layout shift */}
			<div style={{ marginTop: '30px', width: '100%', minHeight: '80px' }}>
				{!realtimeStatus && loadingProgress >= MAX_PROGRESS && (
					<>
						<div style={{
							textAlign: 'center',
							color: '#f44336',
							fontSize: '16px',
							fontWeight: 'bold',
							marginBottom: '10px'
						}}>
							Failed to process order
						</div>
						<div style={{
							textAlign: 'center',
							color: '#666',
							fontSize: '14px',
							marginBottom: '30px'
						}}>
							Please try again.
						</div>
					</>
				)}
			</div>
		</div>
	);
}

export default LoadingProgressIndicator;